<script setup>
import { formatLastUpdated } from '@/utils/dateFormatter'
import { languageToFlagCode } from '@/utils/languageToFlagCode'
import { computed } from 'vue'

const props = defineProps({
  conversation: {
    type: Object,
    required: true
  },
  isSelected: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['select', 'delete'])

function handleSelect () {
  emit('select', props.conversation.conversation_id)
}

function handleDelete (event) {
  event.stopPropagation()
  emit('delete', props.conversation.conversation_id)
}

const formattedLastUpdate = computed(() => {
  return formatLastUpdated(props.conversation.last_updated)
})
</script>

<template>
  <div @click="handleSelect" :class="[
    'p-2 rounded-md mb-2 cursor-pointer border flex flex-col gap-1',
    isSelected ? 'bg-paletteDarkGreen bg-opacity-20 border-paletteDarkGreen' : 'bg-gray-50 border-gray-200'
  ]">
    <!-- First row: Flag, Title, Difficulty Badge, Delete Icon -->
    <div class="flex items-center justify-between">
      <!-- Left side: Flag + Title -->
      <div class="flex items-center">
        <!-- Language Flag or Arabic Image -->
        <img v-if="conversation.language === 'arabic'" src="@/assets/arabic.png" :alt="$t('conversationsSidebar.arabicAlt')" class="flag-icon" />
        <span v-else :class="['flag-icon', `flag-icon-${languageToFlagCode(conversation.language)}`]"></span>

        <!-- Title -->
        <div class="ml-2 text-md font-light">{{ conversation.scenario }}</div>
      </div>

      <!-- Right side: Difficulty Badge & Delete Icon -->
      <div class="flex items-center gap-x-2 ml-auto">
        <!-- Difficulty Badge -->
        <div class="px-2.5 py-0.5 text-sm text-black font-light rounded-md flex items-center" :class="{
          'bg-paletteOrange bg-opacity-60': conversation.difficulty === 'hard',
          'bg-paletteYellow bg-opacity-60': conversation.difficulty === 'medium',
          'bg-paletteLightGreen bg-opacity-60': conversation.difficulty === 'easy'
        }">
          {{ $t(`conversationsSidebar.difficulty.${conversation.difficulty}`) }}
        </div>

        <!-- Delete icon -->
        <button @click="handleDelete"
          class="text-gray-500 text-lg flex items-center hover:text-red-600 transition pb-1">
          {{ $t('conversationsSidebar.deleteIcon') }}
        </button>
      </div>
    </div>

    <!-- Second row -->
    <div class="flex items-center justify-between">
      <!-- Left side: Last active -->
      <div class="flex items-center">
        <div v-if="formatLastUpdated(conversation.last_updated) !== null"
          class="text-sm font-light text-gray-500 italic">
          {{ $t('conversationsSidebar.lastActive', { time: $t(formattedLastUpdate.key, formattedLastUpdate) }) }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.flag-icon {
  width: 24px;
  height: 24px;
  margin-right: 8px;
}
</style>
